import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import {
  formatIqd,
  fromMillidinars,
  type MoveTabItemsResult,
  type OutstandingTabDto,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { OpenTabPicker } from '@/features/tabs/OpenTabPicker';
import { api, ApiError } from '@/lib/api';

/**
 * Move to another visitor — put some of one party's unpaid things onto another
 * person's tab.
 *
 * Moving all of it is a merge; moving some of it is a split. There is no
 * separate "merge" verb because there is no separate operation, and a verb that
 * could ONLY move everything would be the more dangerous of the two.
 *
 * ONE JOB. This screen used to carry two more — renaming a walk-in and
 * attaching a tab to a member account — reached by tabs across the top. Neither
 * was ever reachable from anywhere in the app (nothing opened the dialog in
 * that mode), and the owner does not want them: a cashier at this screen is
 * correcting whose things these ARE, not editing a party's name. They are gone
 * rather than hidden, because a mode nobody opens is a mode nobody maintains.
 *
 * What does NOT move is stated on screen rather than only in the API's
 * docblock: a settled bill has a printed receipt and money in the box against a
 * named person, and moving it would rewrite that. A cashier working a
 * part-settled tab needs to know the friend who already paid keeps his bills.
 */
export function MoveVisitorDialog({
  tab,
  onClose,
  onMoved,
  preselect,
}: {
  tab: OutstandingTabDto;
  onClose: () => void;
  onMoved: () => void;
  /**
   * What the cashier had already ticked on the Outstanding grid.
   *
   * Re-ticking a selection they just made is the kind of second step that gets
   * a correction postponed until nobody remembers what belonged to whom.
   */
  preselect?: { sessionIds: readonly string[]; orderBillIds: readonly string[] };
}) {
  const { t, i18n } = useTranslation();
  const { venue } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';
  const money = (md: string) =>
    formatIqd(fromMillidinars(md), { currencySymbol, locale: i18n.language });

  /** Where it goes: an open tab, OR a name with no tab yet. Never both. */
  const [targetTabId, setTargetTabId] = useState<string | null>(null);
  const [targetNewName, setTargetNewName] = useState('');

  const [moveSessions, setMoveSessions] = useState<ReadonlySet<string>>(
    () => new Set(preselect?.sessionIds ?? []),
  );
  const [moveOrders, setMoveOrders] = useState<ReadonlySet<string>>(
    () => new Set(preselect?.orderBillIds ?? []),
  );
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const movable = tab.sessions.length + tab.orders.length;
  const moveCount = moveSessions.size + moveOrders.size;
  const ready =
    reason.trim().length > 0 &&
    (targetTabId !== null || targetNewName.trim().length > 0) &&
    moveCount > 0;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post<MoveTabItemsResult>(`/tabs/${tab.tabId}/move-items`, {
        // Exactly one of the two, which is what the schema demands: an open tab,
        // or a name the server opens one for.
        ...(targetTabId !== null
          ? { targetTabId }
          : { targetVisitorName: targetNewName.trim() }),
        sessionIds: [...moveSessions],
        orderBillIds: [...moveOrders],
        reason: reason.trim(),
      });
      onMoved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('tabs.move.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  const fieldClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-sm text-content outline-none focus:border-primary';

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="max-h-full w-full max-w-md overflow-auto rounded-card border border-subtle bg-raised p-5 shadow-2xl"
      >
        <header className="mb-4 flex items-start gap-3">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-content">{t('tabs.move.title')}</h2>
            <p className="mt-0.5 text-sm text-content-secondary">
              {t('outstanding.changeVisitor.from', { name: tab.name })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.cancel')}
            className="rounded-control p-1 text-content-secondary hover:bg-hover hover:text-content"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        {movable === 0 ? (
          <p className="text-sm text-content-muted">{t('tabs.move.nothingToMove')}</p>
        ) : (
          <div className="space-y-3">
            <div>
              <span className="mb-1 block text-sm text-content-secondary">
                {t('tabs.move.targetLabel')}
              </span>
              {/*
                LIVE PARTIES ONLY: those who owe money, are playing, hold a pass
                or were opened moments ago, by the cafe's rule (the API's
                tab-liveness.ts). That is everyone on this screen plus a few it
                hides — a pass holder who owes nothing, a player whose wallet
                covers the tab, a complimentary sitting — so not every name
                offered is one the cashier can see above.

                This used to list every open tab, which at the counter meant a
                grid full of names nobody in the building answers to; then
                parties present today, which still offered a tab paid up earlier
                that day and never closed. Moving items onto one of those brings
                a dead tab back to life beside the party's real one — the same
                two tabs under one name the cafe showed for 'Yaran staff'
                (owner, 2026-09-23). Anybody else can still be typed as a new
                name.
              */}
              <OpenTabPicker
                value={targetTabId}
                onChange={(id) => {
                  setTargetTabId(id);
                  // The two targets are exclusive; picking one clears the other
                  // so the body can never carry both.
                  if (id !== null) setTargetNewName('');
                }}
                excludeTabId={tab.tabId}
                source="live"
                autoFocus
              />

              {/* ...or somebody who has no tab yet. The common case is not "move
                  these onto Ahmed's tab" but "these are actually Ahmed's", said
                  about a person who walked in with the group and was never
                  checked in. Without this the cashier has to go and open a tab
                  for them first, and the items stay on the wrong name instead. */}
              <div className="mt-2 flex items-center gap-2">
                <span className="shrink-0 text-xs text-content-muted">
                  {t('tabs.move.orNewName')}
                </span>
                <input
                  value={targetNewName}
                  maxLength={120}
                  onChange={(event) => {
                    setTargetNewName(event.target.value);
                    if (event.target.value.trim()) setTargetTabId(null);
                  }}
                  placeholder={t('tabs.move.newNamePlaceholder')}
                  className={fieldClass}
                />
              </div>
            </div>

            <div>
              <div className="mb-1 flex items-center gap-2">
                <span className="flex-1 text-sm text-content-secondary">
                  {t('tabs.move.itemsLabel')}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setMoveSessions(new Set(tab.sessions.map((row) => row.sessionId)));
                    setMoveOrders(new Set(tab.orders.map((row) => row.billId)));
                  }}
                  className="text-xs text-primary underline"
                >
                  {t('tabs.move.selectAll')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMoveSessions(new Set());
                    setMoveOrders(new Set());
                  }}
                  className="text-xs text-content-secondary underline"
                >
                  {t('tabs.move.clearAll')}
                </button>
              </div>

              <ul className="max-h-44 space-y-1 overflow-auto rounded-control border border-subtle p-1.5">
                {tab.sessions.map((session) => (
                  <li key={session.sessionId}>
                    <label className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 hover:bg-hover">
                      <input
                        type="checkbox"
                        checked={moveSessions.has(session.sessionId)}
                        onChange={() =>
                          setMoveSessions((current) => {
                            const next = new Set(current);
                            if (!next.delete(session.sessionId)) next.add(session.sessionId);
                            return next;
                          })
                        }
                        className="h-4 w-4 rounded-sm accent-[var(--brand-primary)]"
                      />
                      <span className="flex-1 truncate text-sm text-content">
                        {session.deviceName}
                      </span>
                      <span className="tabular shrink-0 text-xs text-content-muted">
                        {money(session.totalMd)}
                      </span>
                    </label>
                  </li>
                ))}
                {tab.orders.map((order) => (
                  <li key={order.billId}>
                    <label className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 hover:bg-hover">
                      <input
                        type="checkbox"
                        checked={moveOrders.has(order.billId)}
                        onChange={() =>
                          setMoveOrders((current) => {
                            const next = new Set(current);
                            if (!next.delete(order.billId)) next.add(order.billId);
                            return next;
                          })
                        }
                        className="h-4 w-4 rounded-sm accent-[var(--brand-primary)]"
                      />
                      <span className="flex-1 truncate text-sm text-content">
                        {order.itemSummary || order.invoiceNumber}
                      </span>
                      <span className="tabular shrink-0 text-xs text-content-muted">
                        {money(order.totalMd)}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <label className="mt-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('outstanding.changeVisitor.reasonLabel')}
          </span>
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={300}
            placeholder={t('outstanding.changeVisitor.reasonPlaceholder')}
            className={fieldClass}
          />
        </label>

        <p className="mt-3 rounded-control border border-subtle bg-input px-3 py-2 text-xs text-content-secondary">
          {t('tabs.move.note')}
        </p>

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={!ready || submitting}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-50"
          >
            {t('tabs.move.confirm', { count: moveCount })}
          </button>
        </div>
      </form>
    </div>
  );
}
